import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { PremiumPrice } from 'src/api/internal/schema';
import { TrackingApi } from 'src/api';
import { DownsellTrackingAction } from 'src/api/tracking/types';
import { useUserContext } from 'src/context/user';
import NotificationContext from 'src/context/notification';
import { ROUTES } from 'src/constants/routes.const';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import { getContinueOnPathQueryFromLocation } from 'src/apps/Path/continueOnPathQuery';
import {
  markDownsellToastShown,
  shouldShowDownsellToast,
} from 'src/apps/Monetisation/DownsellToast/downsellToastStorage';
import { useDownsellVariant } from 'src/apps/Monetisation/DownsellToast/useDownsellVariant';
import { useViewItemListEvent } from '../../hooks/useViewItemListEvent';
import { getPlansPageSource } from '../../utils/getPlansPageSource';
import { PlanCard } from '../PlanCard';
import * as S from './styles';

const LOCATION = 'condensed_plans_page' as const;
const COUNTDOWN_SECONDS = 57 * 60 + 13;

const msgs = defineMessages({
  eyebrow: {
    id: 'new_plans_panel_eyebrow',
    defaultMessage: 'MEMRISE PRO',
  },
  title: {
    id: 'new_plans_panel_title',
    defaultMessage: 'Fluent from <em>just {pricePerDay}/day</em>',
  },
  subTitle: {
    id: 'new_plans_panel_subtitle',
    defaultMessage: 'A whole new culture. For less than a <strong>coffee a day</strong>.',
  },
  currency: {
    id: 'new_plans_panel_currency',
    defaultMessage: 'Currency',
  },
  cta: {
    id: 'condensed_plans_cta_unlock',
    defaultMessage: 'Unlock Pro now',
  },
  trustCancel: {
    id: 'new_plans_panel_trust_cancel',
    defaultMessage: 'Cancel anytime',
  },
  trustSecure: {
    id: 'new_plans_panel_trust_secure',
    defaultMessage: 'Secure checkout',
  },
  trustRenew: {
    id: 'new_plans_panel_trust_renew',
    defaultMessage: 'Auto-renews yearly',
  },
  freeLink: {
    id: 'new_plans_panel_free_link',
    defaultMessage: 'Continue with limited free version',
  },
  featureAi: {
    id: 'new_plans_panel_feature_ai',
    defaultMessage: 'AI conversation practice',
  },
  featureGrammar: {
    id: 'new_plans_panel_feature_grammar',
    defaultMessage: 'Grammar & verb drills',
  },
  featureAdfree: {
    id: 'new_plans_panel_feature_adfree',
    defaultMessage: 'Ad-free',
  },
  featureVocab: {
    id: 'new_plans_panel_feature_vocab',
    defaultMessage: 'All vocab unlocked',
  },
  promoBanner: {
    id: 'condensed_plans_promo_countdown',
    defaultMessage: 'Flash sale ends in {countdown}',
  },
  downsellMainLine: {
    id: 'downsell_toast_main_line',
    defaultMessage: 'Pro Annual works out at {pricePerDay} a day',
  },
  downsellSecondaryLine: {
    id: 'downsell_toast_secondary_line',
    defaultMessage: 'Less than a coffee a week. Try free for 3 days.',
  },
  downsellCta: {
    id: 'downsell_toast_cta',
    defaultMessage: 'Try free',
  },
});

export const PlansPanel: FC<{
  prices: PremiumPrice[];
  selectedPrice: PremiumPrice | null;
  setSelectedPrice: (price: PremiumPrice) => void;
  onSubscribe: () => void;
  onClose?: () => void;
}> = ({ prices, selectedPrice, setSelectedPrice, onSubscribe, onClose }) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const isPro = !!user?.isPro;
  const downsellVariant = useDownsellVariant(user?.id);
  const { setNotification } = useContext(NotificationContext);

  useEffect(() => {
    void TrackingApi.trackUserAction('screen_viewed', LOCATION, languagePairId, isPro, {
      source: getPlansPageSource(),
    });
    // Fire only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthlyPlan = prices.find(p => p.paymentPeriod === 'month') || null;
  const yearlyPlan = prices.find(p => p.paymentPeriod === 'year') || null;
  const lifetimePlan = prices.find(p => p.paymentPeriod === 'lifetime') || null;

  const displayedPrices = useMemo(
    () => [lifetimePlan, yearlyPlan, monthlyPlan].filter(Boolean) as PremiumPrice[],
    [lifetimePlan, yearlyPlan, monthlyPlan],
  );

  const viewItemListEvent = useViewItemListEvent(displayedPrices);

  useEffect(() => {
    if (viewItemListEvent) {
      window.dataLayer.push(viewItemListEvent);
    }
  }, [viewItemListEvent]);

  useEffect(() => {
    if (!selectedPrice && lifetimePlan) {
      setSelectedPrice(lifetimePlan);
    } else if (!selectedPrice && displayedPrices[0]) {
      setSelectedPrice(displayedPrices[0]);
    }
  }, [selectedPrice, setSelectedPrice, lifetimePlan, displayedPrices]);

  const currency = displayedPrices[0]?.currency || 'USD';

  const bestDailyPrice = useMemo(() => {
    if (!yearlyPlan) return null;
    return Math.ceil((yearlyPlan.finalPrice * 100) / 365) / 100;
  }, [yearlyPlan]);

  const pricePerDayFormatted = bestDailyPrice
    ? intl.formatNumber(bestDailyPrice, { style: 'currency', currency })
    : '';

  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft(prev => (prev <= 1 ? COUNTDOWN_SECONDS : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const formatCountdown = useCallback((total: number) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, []);

  return (
    <S.PanelContainer data-testid="new-plans-panel">
      <S.Inner>
        <S.Eyebrow>{intl.formatMessage(msgs.eyebrow)}</S.Eyebrow>

        <S.Title>
          {intl.formatMessage(msgs.title, {
            pricePerDay: pricePerDayFormatted,
            em: (...chunks: React.ReactNode[]) => <em key="em">{chunks}</em>,
          })}
        </S.Title>

        <S.SubTitle>
          {intl.formatMessage(msgs.subTitle, {
            strong: (...chunks: React.ReactNode[]) => <strong key="strong">{chunks}</strong>,
          })}
        </S.SubTitle>

        <S.PromoBanner data-testid="promo-banner">
          {intl.formatMessage(msgs.promoBanner, {
            countdown: formatCountdown(secondsLeft),
          })}
        </S.PromoBanner>

        {displayedPrices.map(p => (
          <PlanCard
            key={p.paymentPeriod}
            price={p}
            selected={selectedPrice === p}
            bestMonthlyPrice={monthlyPlan?.finalPrice}
            onClick={() => {
              setSelectedPrice(p);
              void TrackingApi.trackUserAction('plan_selected', LOCATION, languagePairId, isPro, {
                plan_id: p.key,
                plan: p.paymentPeriod,
                price: String(p.finalPrice),
                source: getPlansPageSource(),
              });
            }}
          />
        ))}

        <S.FeaturesLine>
          <span>{intl.formatMessage(msgs.featureAi)}</span>
          <span>{intl.formatMessage(msgs.featureGrammar)}</span>
          <span>{intl.formatMessage(msgs.featureAdfree)}</span>
          <span>{intl.formatMessage(msgs.featureVocab)}</span>
        </S.FeaturesLine>

        <S.Cta
          onClick={() => {
            void TrackingApi.trackUserAction('clicked_subscribe', LOCATION, languagePairId, isPro, {
              plan: selectedPrice?.paymentPeriod || '',
              price: String(selectedPrice?.finalPrice || ''),
              source: getPlansPageSource(),
            });
            onSubscribe();
          }}
          data-testid="subscribe"
        >
          {intl.formatMessage(msgs.cta)}
        </S.Cta>

        <S.TrustRow>
          <span>{intl.formatMessage(msgs.trustCancel)}</span>
          <span>{intl.formatMessage(msgs.trustSecure)}</span>
          <span>{intl.formatMessage(msgs.trustRenew)}</span>
        </S.TrustRow>

        {onClose && (
          <S.FreeLink
            onClick={() => {
              const source = getPlansPageSource();
              void TrackingApi.trackUserAction(
                'clicked_free_link',
                LOCATION,
                languagePairId,
                isPro,
                {
                  source,
                },
              );
              // Leaving the plans paywall for the free version is a dismiss-without-purchase.
              void TrackingApi.trackUserAction(
                'paywall_dismissed',
                LOCATION,
                languagePairId,
                isPro,
                {
                  source,
                },
              );
              if (shouldShowDownsellToast(downsellVariant)) {
                markDownsellToastShown();
                const trackDownsellAction = (action: DownsellTrackingAction) =>
                  void TrackingApi.trackUserAction(action, 'downsell', languagePairId, isPro, {
                    source: 'downsell',
                    variant: 'treatment',
                  });
                trackDownsellAction('screen_viewed');
                setNotification({
                  text: (
                    <>
                      <S.DownsellMainLine>
                        {intl.formatMessage(msgs.downsellMainLine, {
                          pricePerDay: pricePerDayFormatted,
                        })}
                      </S.DownsellMainLine>
                      <S.DownsellSecondaryLine>
                        {intl.formatMessage(msgs.downsellSecondaryLine)}
                      </S.DownsellSecondaryLine>
                    </>
                  ),
                  status: 'info',
                  snackBar: true,
                  hideIcon: true,
                  linkText: intl.formatMessage(msgs.downsellCta),
                  linkHref: stringifyUrl(ROUTES.payment.plans, {
                    ...getContinueOnPathQueryFromLocation(),
                    source: 'downsell',
                  }),
                  onLinkClick: () => trackDownsellAction('clicked_subscribe'),
                  onClick: () => trackDownsellAction('paywall_dismissed'),
                });
              }
              onClose();
            }}
            data-testid="skip-upsell-page"
          >
            {intl.formatMessage(msgs.freeLink)}
          </S.FreeLink>
        )}
      </S.Inner>
    </S.PanelContainer>
  );
};
